import type { ReconFlagRow } from '../utils/reconFlags.types';
import {
  headerSubtitleParts,
  mergeAccountShape,
  userDisplayName,
} from './reconFlagDrawerOverviewDetails';
import {
  formatDateTimeValue,
  isPlainObject,
  readString,
} from './reconFlagDrawerOverviewFormatting';

export type ReconFlagDrawerHeaderModel = {
  clientLine?: string;
  priorityRaw?: string;
  highlightLine?: string;
  custodianAccount?: string;
  createdAtIso: string;
  /** Drawer summary: human-readable type (issue title / display type / flag type). */
  displayType?: string;
  /** Drawer summary: custodian account number (aligned with grid column). */
  custodianRaw?: string;
  /** Drawer summary: formatted `createdAt` for display. */
  createdAtDisplay: string;
  /** Client user id for open-client-profile link when present. */
  clientProfileUserId?: string;
};

export const buildReconFlagDrawerHeader = (
  flag: ReconFlagRow,
): ReconFlagDrawerHeaderModel => {
  const merged = mergeAccountShape(flag);
  const clientLine =
    readString(merged?.clientName as string | undefined) ??
    userDisplayName(flag.account?.user ?? null);

  const details = flag.details;
  const custodianFromDetails = ((): string | undefined => {
    if (!isPlainObject(details)) {
      return undefined;
    }
    const d = details as Record<string, unknown>;
    const fromRoot =
      readString(d.custodianAccountNumber) ??
      readString(d.custodian_account_number);
    if (fromRoot) {
      return fromRoot;
    }
    const acct = d.account;
    if (!isPlainObject(acct)) {
      return undefined;
    }
    return (
      readString(acct.custodianAccountNumber) ??
      readString(acct.custodian_account_number)
    );
  })();

  const custodianAccount =
    flag.account?.custodianAccountNumber ?? custodianFromDetails ?? undefined;

  const priorityRaw = flag.priority ?? undefined;

  const highlightLine = headerSubtitleParts(flag) ?? undefined;
  const displayType =
    highlightLine ??
    readString(flag.type) ??
    readString(flag.objectType) ??
    undefined;

  const custodianRaw = custodianAccount?.trim() || undefined;

  const createdAtDisplay = formatDateTimeValue(flag.firstSeenAt);
  const clientProfileUserId = readString(flag.account?.user?.id);

  return {
    clientLine: clientLine ?? undefined,
    priorityRaw,
    highlightLine,
    custodianAccount: custodianAccount ?? undefined,
    createdAtIso: flag.firstSeenAt,
    displayType,
    custodianRaw,
    createdAtDisplay,
    clientProfileUserId,
  };
};
